import {
	DatagridBackendFilterType,
	DatagridFilterInputType,
	type FilterConfig,
	type FilterValues,
} from "features/datagrid-configs/types";
import { apiStatusFilterOptions } from "../metadata/apiStatusOptions";
import { VideoMetadataFilterField, VideoProviderMetadataFilterField } from "../metadata/types";
import {
	VideoUploadsFilterField,
	VideoUploadsSourceFilterValue,
} from "./videoUploadsFilterConstants";

const sourceFilterOptions = [
	{ value: VideoUploadsSourceFilterValue.All, label: "All" },
	{ value: VideoUploadsSourceFilterValue.Manual, label: "Manual uploaded videos" },
	{ value: VideoUploadsSourceFilterValue.ApiDieLigen, label: "API Die Ligen" },
	{ value: VideoUploadsSourceFilterValue.ApiVeo, label: "API Veo" },
	{ value: VideoUploadsSourceFilterValue.Discover, label: "Discover user videos" },
];

const sourceFilter: FilterConfig = {
	id: VideoUploadsFilterField.Source,
	label: "Source",
	type: DatagridFilterInputType.Select,
	field: "source",
	filterType: DatagridBackendFilterType.Exact,
	options: sourceFilterOptions,
	placeholder: "Source",
};

const veoCameraIdFilter: FilterConfig = {
	id: VideoUploadsFilterField.VeoCameraId,
	label: "Veo camera ID",
	type: DatagridFilterInputType.Text,
	field: "recording_slug",
	filterType: DatagridBackendFilterType.Search,
	placeholder: "Veo camera ID",
	disabled: true,
};

const veoClubhouseFilter: FilterConfig = {
	id: VideoUploadsFilterField.VeoClubhouse,
	label: "Veo clubhouse",
	type: DatagridFilterInputType.Text,
	field: VideoProviderMetadataFilterField.ClubhouseName,
	filterType: DatagridBackendFilterType.Search,
	placeholder: "Veo clubhouse",
};

const veoTeamFilter: FilterConfig = {
	id: VideoUploadsFilterField.VeoTeam,
	label: "Veo team",
	type: DatagridFilterInputType.Text,
	field: "veo_team",
	filterType: DatagridBackendFilterType.Search,
	placeholder: "Veo team",
	disabled: true,
};

const createdAtFilter: FilterConfig = {
	id: VideoUploadsFilterField.CreatedAt,
	label: "Uploaded on",
	type: DatagridFilterInputType.Daterange,
	field: "createdAt",
	filterType: DatagridBackendFilterType.Datetimerange,
	placeholder: "Uploaded on",
};

const uploadedByFilter: FilterConfig = {
	id: VideoUploadsFilterField.UploadedBy,
	label: "Uploaded by",
	type: DatagridFilterInputType.Multiselect,
	field: "uploadedBy",
	filterType: DatagridBackendFilterType.Inarray,
	wideMenu: true,
	placeholder: "Uploaded by",
};

const savedByFilter: FilterConfig = {
	id: VideoUploadsFilterField.SavedBy,
	label: "Saved by",
	type: DatagridFilterInputType.Multiselect,
	field: "metadataAddedBy",
	filterType: DatagridBackendFilterType.Inarray,
	wideMenu: true,
	placeholder: "Saved by",
};

const apiStatusFilter: FilterConfig = {
	id: VideoUploadsFilterField.ApiStatus,
	label: "API status",
	type: DatagridFilterInputType.Select,
	field: "apiStatus",
	filterType: DatagridBackendFilterType.Exact,
	options: [...apiStatusFilterOptions],
	placeholder: "API status",
};

const gameIdFilter: FilterConfig = {
	id: VideoUploadsFilterField.GameId,
	label: "Game ID",
	type: DatagridFilterInputType.Text,
	field: VideoMetadataFilterField.GameId,
	filterType: DatagridBackendFilterType.Exact,
	placeholder: "Game ID",
	numeric: true,
};

function isVeoSourceSelected(filterValues: FilterValues): boolean {
	return filterValues[VideoUploadsFilterField.Source] === VideoUploadsSourceFilterValue.ApiVeo;
}

function veoSourceFilters(): FilterConfig[] {
	return [veoCameraIdFilter, veoClubhouseFilter, veoTeamFilter];
}

export function getVideoUploadsFilters(filterValues: FilterValues): FilterConfig[] {
	const filters: FilterConfig[] = [
		sourceFilter,
		...(isVeoSourceSelected(filterValues) ? veoSourceFilters() : []),
		createdAtFilter,
		uploadedByFilter,
		savedByFilter,
		apiStatusFilter,
		gameIdFilter,
	];

	return filters;
}

export function shouldClearVeoSourceFilters(
	previousSource: unknown,
	nextSource: unknown,
): boolean {
	return (
		previousSource === VideoUploadsSourceFilterValue.ApiVeo &&
		nextSource !== VideoUploadsSourceFilterValue.ApiVeo
	);
}

export const veoSourceFilterFieldIds = [
	VideoUploadsFilterField.VeoCameraId,
	VideoUploadsFilterField.VeoClubhouse,
	VideoUploadsFilterField.VeoTeam,
] as const;
